import Vue from "vue";
import App from "./App.vue";
import vuetify from "./plugins/vuetify";
import router from "./router";
import customer_router from "./customer_router";
import admin_router from "./admin_router";
import login_router from "./login_router";
import store from "./store/store";
import Vuebar from "vuebar";
import "./plugins/base";
import VueSkycons from "vue-skycons"; 
import InstantSearch from "vue-instantsearch";
import JsonCSV from 'vue-json-csv';
import VueSweetalert2 from 'vue-sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import Donut from 'vue-css-donut-chart';
import 'vue-css-donut-chart/dist/vcdonut.css';
import PrimeVue from 'primevue/config';
import axios from 'axios';
import { Auth0Plugin } from "./auth";
import {
  AUTH0_CALLBACK_ROUTE,
  AUTH_ROUTE_PREFIX,
  LOGIN_ROUTE,
  clearPoSession,
  installPoBearerInterceptor,
  poToken,
  validatePoSession,
} from "./auth/poSession";

const auth0Domain = process.env.VUE_APP_AUTH0_DOMAIN || "";
const auth0ClientId = process.env.VUE_APP_AUTH0_CLIENT_ID || "";

Vue.use(Auth0Plugin, {
  domain: auth0Domain,
  clientId: auth0ClientId,
  redirectUri: window.location.origin + AUTH0_CALLBACK_ROUTE,
  onRedirectCallback: appState => {
    // Clear URL parameters immediately
    window.history.replaceState({}, document.title, window.location.pathname);
    
    // If targetUrl exists, we'll use it if the app is already mounted
    // Otherwise bootstrap will handle the initial view based on auth state
    if (appState && appState.targetUrl && router.app) {
      router.push(appState.targetUrl);
    }
  }
});

Vue.use(PrimeVue);

Vue.use(Donut);

Vue.use(VueSweetalert2);

Vue.component('downloadCsv', JsonCSV);

Vue.use(VueSkycons, {
  color: "#1e88e5",
});
Vue.use(InstantSearch);
Vue.config.productionTip = false;
Vue.use(Vuebar);

Vue.prototype.$adminapiurl = '/';
Vue.prototype.$apiurl = '/';

installPoBearerInterceptor();

// Only one auth-check may be in flight at a time. PHP holds the session file
// lock for the whole of a request, so while a slow endpoint (batch creation, a
// big report) is running every auth-check blocks behind it — and the 5s timer
// kept queueing more, which then all landed at once when the lock was released.
let authCheckInFlight = false;

function runAuthCheck() {
  if (authCheckInFlight) return;
  if (!poToken()) return;

  authCheckInFlight = true;
  validatePoSession(true)
    .catch(() => {
      if (!window.location.pathname.startsWith(AUTH_ROUTE_PREFIX)) {
        window.location.href = LOGIN_ROUTE;
      }
    })
    .finally(() => {
      authCheckInFlight = false;
    });
}

document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") {
    runAuthCheck();
  }
});
let sessionInterval = null;

function startSessionPolling() {
  if (sessionInterval) return;

  sessionInterval = setInterval(() => {
    // A hidden tab does not need polling — the visibilitychange handler above
    // re-checks as soon as the user comes back.
    if (document.visibilityState === "hidden") return;

    runAuthCheck();
  }, 60000);
}

if (poToken()) {
  startSessionPolling();
}


axios.interceptors.response.use(
  response => response,
  error => {
    let raw = error.response?.data;


    // Body-parsed status takes priority over HTTP status. The backend's
    // LoggerBehavior mangles 401 responses into HTTP 500 with a print_r'd
    // exception body — the actual auth status only survives in the body's
    // "[status] => 401" line. HTTP status is the fallback for well-formed
    // responses (real 500s, plain 401s, etc.).
    const status = (typeof raw === "object" && raw !== null && (raw.status || raw.code))
      || (typeof raw === "string" && (() => {
          const match = raw.match(/\[status\]\s*=>\s*(\d+)/);
          return match ? parseInt(match[1], 10) : null;
        })())
      || error.response?.status;

    if (status === 401) {
      clearPoSession();
      if (!window.location.pathname.startsWith(AUTH_ROUTE_PREFIX)) {
        window.location.href = LOGIN_ROUTE;
      }
    }

    // A normal fine-grained 403 does not invalidate a valid session. The
    // calling view may present its own permission-denied state.

    return Promise.reject(error);
  }
);


async function bootstrap() {
  try {
    const res = await axios.get(Vue.prototype.$apiurl + "api/web/site/get-maintenance-status")
    const status = res.data.data[0].status // "ON" or "OFF"
    const maintenance = status === 'ON'

    store.commit('SET_MAINTENANCE', maintenance)

  } catch (err) {
    // Maintenance status is advisory; authentication still fails closed below.
  }

  // Wait for Auth0 to finish loading
   const waitForAuth = () => {
     return new Promise(resolve => {
       if (Vue.prototype.$auth && !Vue.prototype.$auth.loading) {
         return resolve();
       }
       const unwatch = Vue.prototype.$auth.$watch("loading", loading => {
         if (!loading) {
           unwatch();
           resolve();
         }
       });
     });
   };

   await waitForAuth();

  let session = null;
  if (poToken()) {
    try {
      session = await validatePoSession(true);
    } catch (error) {
      session = null;
    }
  } else {
    clearPoSession();
  }

  if (session && session.user) {
    await store.dispatch("LogIn", {
      username: session.user.username || session.user.email,
      firstname: session.user.firstname || "",
      lastname: session.user.lastname || "",
      email: session.user.email,
      site_id: session.site_id,
      user_type: session.user.user_type,
      status: session.user.status || "Active",
      avatar: "test.png",
      user_id: session.user.id,
      admin_id: "",
      token: poToken(),
    });
    startSessionPolling();
  }

  let selectedRouter = login_router;
  if (session && session.user) {
    if (session.user.user_type === "Customer") {
      selectedRouter = customer_router;
    } else if (Number(session.site_id) === 0) {
      selectedRouter = admin_router;
    } else {
      selectedRouter = router;
    }
  }

  new Vue({
    vuetify,
    store,
    router: selectedRouter,
    render: h => h(App),
  }).$mount("#app");
}

bootstrap()
